import { setupInstallPrompt } from "../pwa/install_prompt";
import { hydrateApiToken } from "../spa/apiAuth";

function markNativeRuntime() {
  const protocol = window.location?.protocol;
  if (protocol === "capacitor:" || protocol === "ionic:") {
    document.documentElement.dataset.nativeRuntime = "true";
    return;
  }

  if (/\bCapacitor\b/i.test(window.navigator?.userAgent || "")) {
    document.documentElement.dataset.nativeRuntime = "true";
    return;
  }

  const capacitor = window.Capacitor;
  if (!capacitor || typeof capacitor !== "object") return;

  let isNativeRuntime = false;
  if (typeof capacitor.isNativePlatform === "function") {
    try {
      isNativeRuntime = Boolean(capacitor.isNativePlatform());
    } catch (_error) {
      // fall through to additional checks
    }
  }

  if (!isNativeRuntime && typeof capacitor.getPlatform === "function") {
    try {
      isNativeRuntime = capacitor.getPlatform() !== "web";
    } catch (_error) {
      // fall through to platform property checks
    }
  }

  if (!isNativeRuntime) {
    isNativeRuntime = capacitor.platform === "ios" || capacitor.platform === "android";
  }

  if (isNativeRuntime) {
    document.documentElement.dataset.nativeRuntime = "true";
  }
}

async function mountAppShell() {
  markNativeRuntime();
  hydrateApiToken().catch(() => {});

  const node = document.querySelector("[data-app-shell]");
  if (!node || node.dataset.mounted === "true" || node.dataset.mounted === "loading") return;

  const csrfToken = document.querySelector("meta[name='csrf-token']")?.content || "";

  node.dataset.mounted = "loading";

  try {
    // Defer AppShell (and its Chart.js/Leaflet dependencies) on pages that do not render /app.
    const [{ default: React }, { createRoot }, { default: AppShell }] = await Promise.all([
      import("react"),
      import("react-dom/client"),
      import("../spa/AppShell")
    ]);

    createRoot(node).render(
      React.createElement(AppShell, {
        catchesEndpoint: node.dataset.catchesEndpoint || "/api/v1/catches",
        dashboardEndpoint: node.dataset.dashboardEndpoint || "/api/v1/dashboard",
        spotsEndpoint: node.dataset.spotsEndpoint || "/api/v1/fishing_spots",
        profileEndpoint: node.dataset.profileEndpoint || "/api/v1/profile",
        sessionEndpoint: node.dataset.sessionEndpoint || "/api/v1/session",
        csrfToken,
        isAdmin: node.dataset.isAdmin === "true",
        auditLogsPath: node.dataset.auditLogsPath || "",
        initialNotice: node.dataset.notice || "",
        initialAlert: node.dataset.alert || "",
        initialPath: node.dataset.currentPath || window.location.pathname
      })
    );

    node.dataset.mounted = "true";
  } catch (error) {
    node.dataset.mounted = "false";
    console.error("Failed to mount AppShell", error);
  }
}

async function mountSpotsApp() {
  markNativeRuntime();
  hydrateApiToken().catch(() => {});

  const node = document.querySelector("[data-spots-app]");
  if (!node || node.dataset.mounted === "true" || node.dataset.mounted === "loading") return;

  node.dataset.mounted = "loading";

  try {
    const [{ default: React }, { createRoot }, { default: SpotsApp }] = await Promise.all([
      import("react"),
      import("react-dom/client"),
      import("../spots/SpotsApp")
    ]);

    createRoot(node).render(
      React.createElement(SpotsApp, {
        endpoint: node.dataset.spotsEndpoint || "/api/v1/fishing_spots",
        initialPath: node.dataset.currentPath || window.location.pathname,
        isAdmin: node.dataset.isAdmin === "true"
      })
    );

    node.dataset.mounted = "true";
  } catch (error) {
    node.dataset.mounted = "false";
    console.error("Failed to mount SpotsApp", error);
  }
}

window.addEventListener("vite:preloadError", (event) => {
  const storageKey = "imonimon:preload-reloaded";
  if (sessionStorage.getItem(storageKey) === "true") return;

  sessionStorage.setItem(storageKey, "true");
  event.preventDefault();
  window.location.reload();
});

document.addEventListener("DOMContentLoaded", mountAppShell);
document.addEventListener("turbo:load", mountAppShell);
document.addEventListener("DOMContentLoaded", mountSpotsApp);
document.addEventListener("turbo:load", mountSpotsApp);
document.addEventListener("DOMContentLoaded", setupInstallPrompt);
document.addEventListener("turbo:load", setupInstallPrompt);
document.addEventListener("DOMContentLoaded", markNativeRuntime);
document.addEventListener("turbo:load", markNativeRuntime);
